import { useState, useEffect } from 'react';
import { getCityHeroPhoto, CITY_HERO_PHOTOS } from '../constants';
import supabase from '../supabase';

// City-level hero photo, resolved once then cached — always city-wide, never
// district-specific. An earlier version searched per-district (AI-translated
// district name -> image search), but that AI translation step could
// hallucinate a loosely-associated search term (e.g. "內湖區" -> "Taipei Zoo",
// since the zoo happens to sit in that district) and return a completely
// unrepresentative photo (an arctic fox, for the Neihu-district banner, in
// production). City-level search text ("Taipei cityscape skyline") has no
// such ambiguity, so it's used unconditionally instead.
const _cityPhotoCache = {}; // in-memory, avoids redundant localStorage reads within one session
const LS_PFX = 'cityHero_';
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days — city photos don't need to refresh often

function getLocalCache(key) {
  try {
    const item = JSON.parse(localStorage.getItem(LS_PFX + key));
    if (!item || Date.now() > item.exp) { localStorage.removeItem(LS_PFX + key); return undefined; }
    return item.url;
  } catch { return undefined; }
}
function setLocalCache(key, url) {
  try { localStorage.setItem(LS_PFX + key, JSON.stringify({ url, exp: Date.now() + TTL_MS })); } catch {}
}
async function getServerCache(key) {
  try {
    const { data, error } = await supabase.from('ai_cache').select('data,expires_at').eq('cache_key', key).maybeSingle();
    if (error || !data) return undefined;
    if (new Date(data.expires_at) < new Date()) return undefined;
    return data.data?.url ?? null;
  } catch { return undefined; }
}
async function setServerCache(key, url) {
  try {
    await supabase.from('ai_cache').upsert(
      { cache_key: key, data: { url }, expires_at: new Date(Date.now() + TTL_MS).toISOString() },
      { onConflict: 'cache_key' },
    );
  } catch {}
}

// For cities with no entry in CITY_HERO_PHOTOS (only the ~20 busiest cities
// are hardcoded there). cityKey is already the canonical English name by the
// time it reaches this hook (see CityBanner callers), so a direct image
// search works — no AI translation step, no district-name ambiguity.
async function resolveCityPhoto(cityKey) {
  try {
    const r = await fetch(`/api/photo?query=${encodeURIComponent(`${cityKey} cityscape skyline`)}`);
    if (!r.ok) return null;
    const d = await r.json();
    return d?.url || null;
  } catch { return null; }
}

// Returns the static per-city photo immediately (for the ~20 cities that have
// one), then swaps in a resolved city-level photo for every other city — so
// every city gets a representative photo instead of falling back to one
// generic default image. `district` no longer affects the photo (only the
// caller's own label text) — see file header for why.
export default function useDistrictHeroPhoto(cityKey) {
  const fallback = getCityHeroPhoto(cityKey);
  const [src, setSrc] = useState(fallback);
  const hasStaticPhoto = !!CITY_HERO_PHOTOS[cityKey];
  const cacheKey = !hasStaticPhoto && cityKey ? `city_hero_v1_${cityKey}` : null;

  useEffect(() => {
    setSrc(getCityHeroPhoto(cityKey));
    if (!cacheKey) return;

    if (_cityPhotoCache[cacheKey] !== undefined) {
      if (_cityPhotoCache[cacheKey]) setSrc(_cityPhotoCache[cacheKey]);
      return;
    }
    const local = getLocalCache(cacheKey);
    if (local !== undefined) {
      _cityPhotoCache[cacheKey] = local;
      if (local) setSrc(local);
      return;
    }

    let cancelled = false;
    (async () => {
      const server = await getServerCache(cacheKey);
      if (server !== undefined) {
        _cityPhotoCache[cacheKey] = server;
        setLocalCache(cacheKey, server);
        if (!cancelled && server) setSrc(server);
        return;
      }
      const url = await resolveCityPhoto(cityKey);
      _cityPhotoCache[cacheKey] = url;
      setLocalCache(cacheKey, url);
      setServerCache(cacheKey, url); // fire-and-forget — shares the result with all future visitors
      if (!cancelled && url) setSrc(url);
    })();
    return () => { cancelled = true; };
  }, [cacheKey, cityKey, hasStaticPhoto]);

  return src;
}
